import React, { useState, useEffect } from 'react';

interface APICall {
  id: string;
  timestamp: Date;
  method: string;
  url: string;
  status?: number;
  duration?: number;
  requestBody?: any;
  responseData?: any;
  error?: string;
}

interface ComponentState {
  name: string;
  props: Record<string, any>;
  state: Record<string, any>;
  hooks: string[];
}

interface DevModeProps {
  currentMode: string;
  activeComponents: ComponentState[];
  apiCalls: APICall[];
  userState: {
    id?: string;
    balance: number;
    activeBets: number;
    totalWagered: number;
  };
  marketState: {
    totalMarkets: number;
    activeMarkets: number;
    totalVolume: number;
    currentMarket?: any;
  };
}

export default function DevMode({
  currentMode,
  activeComponents,
  apiCalls,
  userState,
  marketState
}: DevModeProps) {
  const [isMinimized, setIsMinimized] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'components' | 'api' | 'state' | 'flow'>('components');
  const [isRecording, setIsRecording] = useState(true);

  // Check for devMode query param
  const urlParams = new URLSearchParams(window.location.search);
  const devMode = urlParams.get('devMode') === '1';

  if (!devMode) return null;

  return (
    <>
      {/* Matrix rain effect */}
      <div className="fixed inset-0 pointer-events-none z-40 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute text-green-400 text-xs font-mono animate-matrix"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 4}s`
              }}
            >
              {Math.random() > 0.5 ? '₿ $$$' : '📈 📊'}
            </div>
          ))}
        </div>
      </div>

      {/* Main Dev Panel */}
      <div
        className={`fixed bottom-4 right-4 z-50 transition-all duration-300 ${
          isMinimized ? 'w-auto' : 'w-96'
        }`}
      >
        {isMinimized ? (
          <button
            onClick={() => setIsMinimized(false)}
            className="p-3 bg-gradient-to-r from-green-600 to-orange-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          </button>
        ) : (
          <div
            className="bg-black/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-green-500/30 overflow-hidden"
            style={{
              background: 'linear-gradient(145deg, rgba(16,185,129,0.1), rgba(0,0,0,0.95), rgba(249,115,22,0.1))'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-green-500/20">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="font-mono text-sm text-green-400">FutrBet DevMode</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsRecording(!isRecording)}
                  className={`p-1 rounded ${isRecording ? 'text-red-400' : 'text-gray-400'}`}
                >
                  <div className={`w-2 h-2 rounded-full ${isRecording ? 'bg-red-400 animate-pulse' : 'bg-gray-400'}`}></div>
                </button>
                <button
                  onClick={() => setIsMinimized(true)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-green-500/20">
              {(['components', 'api', 'state', 'flow'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setSelectedTab(tab)}
                  className={`flex-1 px-3 py-2 text-xs font-mono transition-all ${
                    selectedTab === tab
                      ? 'bg-green-500/20 text-green-400 border-b-2 border-green-400'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {tab.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="p-4 max-h-96 overflow-y-auto">
              {selectedTab === 'components' && (
                <div className="space-y-3">
                  <div className="text-xs font-mono text-gray-400">
                    Mode: <span className="text-green-400">{currentMode}</span>
                  </div>
                  <div className="space-y-2">
                    {activeComponents.map((comp, idx) => (
                      <div key={idx} className="p-2 bg-green-500/10 rounded-lg border border-green-500/20">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono text-green-400">{comp.name}</span>
                          <span className="text-xs text-gray-500">{comp.hooks.length} hooks</span>
                        </div>
                        {Object.keys(comp.state).length > 0 && (
                          <div className="mt-1 text-xs text-gray-400">
                            State: {JSON.stringify(comp.state, null, 2).substring(0, 50)}...
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedTab === 'api' && (
                <div className="space-y-2">
                  {apiCalls.length === 0 ? (
                    <div className="text-xs text-gray-500 text-center py-4">No API calls yet</div>
                  ) : (
                    apiCalls.slice(-5).reverse().map((call) => (
                      <div key={call.id} className="p-2 bg-orange-500/10 rounded-lg border border-orange-500/20">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-mono ${
                            call.method === 'GET' ? 'text-blue-400' :
                            call.method === 'POST' ? 'text-green-400' :
                            'text-orange-400'
                          }`}>
                            {call.method}
                          </span>
                          <span className={`text-xs ${
                            call.status && call.status < 300 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {call.status || 'pending'} • {call.duration || '-'}ms
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 truncate">
                          {call.url.replace('https://futr-bet-api.b-9f2.workers.dev', '')}
                        </div>
                        {call.error && (
                          <div className="text-xs text-red-400 mt-1">{call.error}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {selectedTab === 'state' && (
                <div className="space-y-4">
                  <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                    <div className="text-xs font-mono text-green-400 mb-2">User State</div>
                    <div className="space-y-1 text-xs text-gray-400">
                      <div>Balance: <span className="text-white">${userState.balance}</span></div>
                      <div>Active Bets: <span className="text-white">{userState.activeBets}</span></div>
                      <div>Total Wagered: <span className="text-white">${userState.totalWagered}</span></div>
                    </div>
                  </div>

                  <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                    <div className="text-xs font-mono text-orange-400 mb-2">Market State</div>
                    <div className="space-y-1 text-xs text-gray-400">
                      <div>Total Markets: <span className="text-white">{marketState.totalMarkets}</span></div>
                      <div>Active: <span className="text-white">{marketState.activeMarkets}</span></div>
                      <div>Volume: <span className="text-white">${marketState.totalVolume}</span></div>
                    </div>
                  </div>
                </div>
              )}

              {selectedTab === 'flow' && (
                <div className="space-y-3">
                  <div className="text-xs font-mono text-gray-400">Data Flow</div>
                  <div className="space-y-2">
                    <FlowStep
                      step={1}
                      label="User Action"
                      description="Create market / Place bet"
                      active={true}
                    />
                    <FlowStep
                      step={2}
                      label="Frontend"
                      description="React + State Management"
                      active={apiCalls.length > 0}
                    />
                    <FlowStep
                      step={3}
                      label="API Call"
                      description="CloudFlare Worker"
                      active={apiCalls.some(c => c.status)}
                    />
                    <FlowStep
                      step={4}
                      label="Database"
                      description="D1 SQLite"
                      active={apiCalls.some(c => c.status === 200)}
                    />
                    <FlowStep
                      step={5}
                      label="Response"
                      description="Update UI State"
                      active={apiCalls.some(c => c.status === 200)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer Stats */}
            <div className="p-3 border-t border-green-500/20 flex justify-between text-xs text-gray-400">
              <span>API: {apiCalls.length} calls</span>
              <span>Components: {activeComponents.length}</span>
              <span>v0.1.0</span>
            </div>
          </div>
        )}
      </div>

      {/* Component Overlay Indicators */}
      {!isMinimized && selectedTab === 'components' && (
        <div className="fixed inset-0 pointer-events-none z-30">
          {activeComponents.map((comp, idx) => (
            <div
              key={idx}
              className="absolute border-2 border-dashed border-green-400/30 rounded-lg"
              style={{
                // These would be calculated based on actual component positions
                top: `${20 + idx * 100}px`,
                left: '20px',
                width: '200px',
                height: '80px'
              }}
            >
              <div className="absolute -top-6 left-0 px-2 py-1 bg-green-600 text-white text-xs rounded">
                {comp.name}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function FlowStep({ step, label, description, active }: {
  step: number;
  label: string;
  description: string;
  active: boolean;
}) {
  return (
    <div className={`flex items-center gap-3 p-2 rounded-lg transition-all ${
      active ? 'bg-green-500/10 border border-green-500/20' : 'opacity-50'
    }`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
        active ? 'bg-green-500 text-black' : 'bg-gray-700 text-gray-400'
      }`}>
        {step}
      </div>
      <div className="flex-1">
        <div className={`text-xs font-mono ${active ? 'text-green-400' : 'text-gray-500'}`}>
          {label}
        </div>
        <div className="text-xs text-gray-500">{description}</div>
      </div>
    </div>
  );
}